import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  MapPin,
  Store,
  ShoppingBag,
  Scissors,
  Search,
  Plus,
  LayoutGrid,
  List,
  X,
  ArrowRight,
} from 'lucide-react';
import styles from './servicesCatalog.module.css';
import { useProducts } from '../../api';
import { addToCart } from '../../redux/slice/cartSlice';
import { getShopLatLng, distanceKm as haversineKm, formatDistanceKm } from '../../utils/geo';
import { shopMatchesSelectedCity } from '../../utils/locationPreference';
import { useServicesLocation } from '../../hooks/useServicesLocation';
import FallbackImage from '../../components/brand/FallbackImage';

const ServicesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tagFilter = (searchParams.get('tag') || '').trim();
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart?.items || []);

  const { data: products = [], isLoading: loading } = useProducts();
  const { userPos, selectedCityId, selectedCityName, openLocationModal } = useServicesLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  const cartCount = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (item.quantity || 1), 0);
  }, [cartItems]);

  const cartTotal = useMemo(() => {
    return cartItems.reduce((acc, item) => acc + (item.price || 0) * (item.quantity || 1), 0);
  }, [cartItems]);

  const clearTagFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tag');
      return next;
    });
  };

  useEffect(() => {
    if (sortBy === 'distance' && !userPos) {
      setSortBy('name');
    }
  }, [sortBy, userPos]);

  const productsWithMeta = useMemo(() => {
    return products.map((p) => {
      const shop = typeof p.shop === 'object' && p.shop ? p.shop : null;
      const coords = getShopLatLng(shop);
      let dist = null;
      if (userPos && coords) {
        dist = haversineKm(userPos.lat, userPos.lng, coords.lat, coords.lng);
      }
      const vendorName = shop?.owner && typeof shop.owner === 'object' ? shop.owner.name : null;
      return {
        ...p,
        _shop: shop,
        _distanceKm: dist,
        _vendorName: vendorName,
      };
    });
  }, [products, userPos]);

  const handleCardNavigate = useCallback(
    (productId) => (e) => {
      if (e?.target?.closest?.('button')) return;
      navigate(`/services/${productId}`);
    },
    [navigate]
  );

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    const sid =
      product._shop?._id ?? (typeof product.shop === 'string' ? product.shop : product.shop?._id);
    dispatch(
      addToCart({
        productId: product._id,
        shopId: sid,
        name: product.name,
        price: product.price,
        image: product.images && product.images[0],
        quantity: 1,
        variants: product.variants || {},
      })
    );
    toast.success(`${product.name} added to cart`);
  };

  const filteredProducts = useMemo(() => {
    return productsWithMeta
      .filter((product) => {
        if (selectedCityId && !shopMatchesSelectedCity(product._shop, selectedCityId)) {
          return false;
        }
        const matchesSearch =
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product._shop?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product._vendorName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
        const matchesTag =
          !tagFilter || (product.tags || []).some((t) => String(t).trim() === tagFilter);
        return matchesSearch && matchesCategory && matchesTag;
      })
      .sort((a, b) => {
        switch (sortBy) {
          case 'distance': {
            const da = a._distanceKm ?? Infinity;
            const db = b._distanceKm ?? Infinity;
            return da - db;
          }
          case 'price-low':
            return a.price - b.price;
          case 'price-high':
            return b.price - a.price;
          case 'name':
          default:
            return a.name.localeCompare(b.name);
        }
      });
  }, [productsWithMeta, searchTerm, selectedCategory, sortBy, selectedCityId, tagFilter]);

  const categories = useMemo(
    () => ['all', ...new Set((products || []).map((product) => product.category).filter(Boolean))],
    [products]
  );

  return (
    <div className={styles.page}>
      {/* Compact Header Bar */}
      <div className={styles.compactHero}>
        <div className={styles.heroLeft}>
          <div className={styles.heroIconBox}>
            <Scissors size={20} />
          </div>
          <div>
            <h1 className={styles.heroTitle}>Services &amp; Treatments</h1>
            <p className={styles.heroSubtitle}>
              Explore verified haircuts, styling, and salon treatments near you
            </p>
          </div>
        </div>

        <div className={styles.heroRight}>
          <button
            type="button"
            className={styles.heroLocationBtn}
            onClick={openLocationModal}
            title="Filter by city"
          >
            <MapPin size={14} style={{ color: '#f87171' }} />
            <span>{selectedCityName || 'All Cities'}</span>
          </button>
          <span className={styles.heroCountBadge}>{filteredProducts.length} Available</span>
        </div>
      </div>

      {/* Unified Toolbar */}
      <div className={styles.toolbarCard}>
        <div className={styles.searchWrap}>
          <Search size={15} style={{ color: '#94a3b8' }} />
          <input
            type="text"
            placeholder="Search treatments, haircuts, styling, salon names…"
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              type="button"
              className={styles.clearSearchBtn}
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className={styles.toolbarControls}>
          <select
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Sort: Name (A–Z)</option>
            {userPos && <option value="distance">Sort: Nearest</option>}
            <option value="price-low">Sort: Price (Low → High)</option>
            <option value="price-high">Sort: Price (High → Low)</option>
          </select>

          <div className={styles.viewToggleGroup}>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${viewMode === 'grid' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              className={`${styles.viewToggleBtn} ${viewMode === 'list' ? styles.viewToggleBtnActive : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <List size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Category Pills Filter */}
      {categories.length > 1 && (
        <div className={styles.categoryPills}>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              className={`${styles.categoryPill} ${
                selectedCategory === cat ? styles.categoryPillActive : ''
              }`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'all' ? 'All Services' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* Results Status Bar */}
      <div className={styles.statusBar}>
        <span>
          Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'service' : 'services'}
          {selectedCityName ? ` in ${selectedCityName}` : ''}
        </span>
        {tagFilter && (
          <button type="button" className={styles.tagFilterPill} onClick={clearTagFilter}>
            Tag: {tagFilter} <X size={12} />
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading ? (
        <div className={styles.emptyState}>
          <Scissors
            size={28}
            style={{ color: '#dc2626', margin: '0 auto 8px', animation: 'pulse 1.5s infinite' }}
          />
          <div className={styles.emptyTitle}>Loading Services Catalog…</div>
          <p className={styles.emptyText}>Fetching live salon treatments and pricing.</p>
        </div>
      ) : filteredProducts.length > 0 ? (
        /* Render Grid or List View */
        viewMode === 'grid' ? (
          <div className={styles.compactGrid}>
            {filteredProducts.map((product) => {
              const distanceText =
                userPos && product._distanceKm != null
                  ? formatDistanceKm(product._distanceKm)
                  : null;

              return (
                <div
                  key={product._id}
                  className={styles.compactCard}
                  onClick={handleCardNavigate(product._id)}
                >
                  <div className={styles.compactMedia}>
                    <FallbackImage
                      src={product.images?.[0]}
                      alt={product.name}
                      className={styles.compactImg}
                    />
                    {product.category && (
                      <span className={styles.compactCatTag}>{product.category}</span>
                    )}
                  </div>

                  <div className={styles.compactBody}>
                    <div className={styles.compactTitleRow}>
                      <h3 className={styles.compactTitle} title={product.name}>
                        {product.name}
                      </h3>
                      <span className={styles.compactPrice}>₹{product.price}</span>
                    </div>

                    <div className={styles.compactMetaRow}>
                      <span className={styles.compactSalonInfo} title={product._shop?.name}>
                        <Store size={12} style={{ color: '#dc2626', flexShrink: 0 }} />
                        <span>{product._shop?.name || 'Salon'}</span>
                      </span>
                      {distanceText && (
                        <span className={styles.compactDist}>
                          <MapPin size={11} />
                          <span>{distanceText}</span>
                        </span>
                      )}
                    </div>

                    <div className={styles.compactFooter}>
                      <button
                        type="button"
                        className={styles.compactBtnDetails}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/services/${product._id}`);
                        }}
                      >
                        Details
                      </button>

                      <button
                        type="button"
                        className={styles.compactBtnAdd}
                        onClick={(e) => handleAddToCart(product, e)}
                      >
                        <Plus size={13} />
                        <span>Add to Cart</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.compactList}>
            {filteredProducts.map((product) => {
              const distanceText =
                userPos && product._distanceKm != null
                  ? formatDistanceKm(product._distanceKm)
                  : null;

              return (
                <div
                  key={product._id}
                  className={styles.listRow}
                  onClick={handleCardNavigate(product._id)}
                >
                  <div className={styles.listLeft}>
                    <div className={styles.listThumbnail}>
                      <FallbackImage
                        src={product.images?.[0]}
                        alt={product.name}
                        className={styles.listImg}
                      />
                    </div>
                    <div className={styles.listContent}>
                      <div className={styles.listTitleRow}>
                        <h3 className={styles.listTitle}>{product.name}</h3>
                        {product.category && (
                          <span className={styles.listCatBadge}>{product.category}</span>
                        )}
                      </div>
                      <div className={styles.listMeta}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <Store size={12} style={{ color: '#dc2626' }} />
                          <span>{product._shop?.name || 'Salon'}</span>
                        </span>
                        {distanceText && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#94a3b8',
                            }}
                          >
                            <MapPin size={11} />
                            <span>{distanceText}</span>
                          </span>
                        )}
                      </div>
                      {product.description && (
                        <p className={styles.listDesc}>{product.description}</p>
                      )}
                    </div>
                  </div>

                  <div className={styles.listRight}>
                    <div className={styles.listPrice}>₹{product.price}</div>
                    <button
                      type="button"
                      className={styles.compactBtnAdd}
                      onClick={(e) => handleAddToCart(product, e)}
                    >
                      <Plus size={13} />
                      <span>Add to Cart</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className={styles.emptyState}>
          <ShoppingBag size={36} style={{ color: '#94a3b8', margin: '0 auto 8px' }} />
          <div className={styles.emptyTitle}>No Services Found</div>
          <p className={styles.emptyText}>
            No services match your search or filter criteria. Try resetting filters.
          </p>
          <button
            type="button"
            className={styles.compactBtnAdd}
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              clearTagFilter();
            }}
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* Floating Cart Indicator */}
      {cartCount > 0 && (
        <div className={styles.floatingCartBar}>
          <div className={styles.floatingCartInfo}>
            <span className={styles.floatingCartBadge}>{cartCount}</span>
            <span>Cart Total: ₹{cartTotal}</span>
          </div>
          <Link to="/cart" className={styles.floatingCartLink}>
            <span>View Cart</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
};

export default ServicesPage;
